import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { Modal } from "react-bootstrap";
import { useAuth } from "../../context/LoginContext/AuthContext";
import useCreatePaymentOrder from "../../../hooks/payment/useCreatePaymentOrder";
import useConfirmStripePayment from "../../../hooks/payment/useConfirmStripePayment";
import SuccessModal from "../../common/SuccessModal";

// Initialize Stripe outside of component to avoid recreating on each render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * Stripe Checkout Form Component
 * Handles the actual payment form and submission inside the modal
 */
const CheckoutForm = ({
    invoiceId,
    invoice,
    amount,
    onSuccess,
    onCancel,
    onError,
    currency,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const { confirmPayment: confirmPaymentOnBackend } = useConfirmStripePayment();

    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const handlePayClick = async () => {
        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setErrorMessage("");

        try {
            // Step 1: Trigger form validation and wallet collection
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setErrorMessage(submitError.message);
                setIsProcessing(false);
                return;
            }

            // Step 2: Confirm payment with Stripe
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/payment/success`,
                },
                redirect: "if_required",
            });

            if (error) {
                setErrorMessage(error.message);
                setIsProcessing(false);
                return;
            }
            console.log("STRIPE PAYMENT INTENT")
            console.log(paymentIntent)

            // Payment succeeded without redirect
            if (paymentIntent && paymentIntent.status === "succeeded") {
                // Confirm payment on backend
                const confirmResult = await confirmPaymentOnBackend({
                    invoice_id: invoiceId,
                    payment_intent: paymentIntent.id,
                    amount: paymentIntent.amount,
                    currency: paymentIntent.currency,
                });

                if (confirmResult) {
                    onSuccess();
                } else {
                    onError("Payment verification failed. Please contact support.");
                }
            } else if (paymentIntent && paymentIntent.status === "requires_action") {
                setErrorMessage("Additional authentication required. Please complete the verification.");
                setIsProcessing(false);
            }
        } catch (err) {
            console.error("Payment error:", err);
            setErrorMessage(err.message || "Payment failed. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="stripe-checkout-form">
            <PaymentElement />

            {errorMessage && (
                <div className="alert alert-danger mt-3" role="alert">
                    {errorMessage}
                </div>
            )}

            <div className="d-grid gap-2 mt-4">
                <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    onClick={handlePayClick}
                    disabled={!stripe || !elements || isProcessing}
                >
                    {isProcessing ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                            ></span>
                            Processing...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-lock me-2"></i>
                            Pay {currency === "INR" ? "₹" : "$"}{parseFloat(amount).toFixed(2)}
                        </>
                    )}
                </button>

                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onCancel}
                    disabled={isProcessing}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

/**
 * StripePaymentButton Component
 *
 * Handles Stripe payment initiation with a popup modal.
 * Similar UX to Razorpay popup.
 */
export const StripePaymentButton = ({
    invoiceId,
    invoice,
    amount,
    onSuccess,
    onCancel,
    disabled = false,
    currency,
}) => {
    const { user } = useAuth();
    const { createPaymentOrder, error: createPaymentOrderError } =
        useCreatePaymentOrder();

    const [isProcessing, setIsProcessing] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [clientSecret, setClientSecret] = useState("");

    // Result modal state
    const [showResultModal, setShowResultModal] = useState(false);
    const [resultModalConfig, setResultModalConfig] = useState({
        type: "success",
        title: "Success",
        message: "",
    });

    /**
     * Show success/error result modal
     */
    const displayResultModal = (type, title, message) => {
        setResultModalConfig({ type, title, message });
        setShowResultModal(true);
    };

    /**
     * Handle result modal close
     */
    const handleResultModalClose = () => {
        setShowResultModal(false);
        if (resultModalConfig.type === "success" && onSuccess) {
            onSuccess();
        }
    };

    /**
     * Handle payment modal cancel
     */
    const handlePaymentModalCancel = () => {
        setShowPaymentModal(false);
        setClientSecret("");
        if (onCancel) {
            onCancel();
        }
    };

    /**
     * Handle payment success (from CheckoutForm)
     */
    const handlePaymentSuccess = () => {
        setShowPaymentModal(false);
        const amountFloat = parseFloat(amount);
        displayResultModal(
            "success",
            "Payment Successful",
            `Your payment of ${currency === "INR" ? "₹" : "$"}${amountFloat.toFixed(2)} has been processed successfully!`
        );
    };

    /**
     * Handle payment error (from CheckoutForm)
     */
    const handlePaymentError = (errorMessage) => {
        setShowPaymentModal(false);
        displayResultModal(
            "error",
            "Payment Failed",
            errorMessage || "Payment failed. Please try again."
        );
    };

    /**
     * Initiates Stripe payment flow - opens the modal
     */
    const handleStripePayment = async () => {
        const amountFloat = parseFloat(amount);

        if (isNaN(amountFloat) || amountFloat <= 0) {
            displayResultModal("error", "Error", "Invalid payment amount");
            return;
        }

        setIsProcessing(true);

        try {
            // Create Stripe payment intent via backend
            const orderResult = await createPaymentOrder({
                invoice_id: invoiceId,
                gateway: "stripe",
                amount: amountFloat,
                currency: currency,
            });

            if (!orderResult || !orderResult.client_secret) {
                displayResultModal(
                    "error",
                    "Error",
                    createPaymentOrderError || "Failed to create payment order"
                );
                setIsProcessing(false);
                return;
            }

            // Store client secret and open the payment modal
            setClientSecret(orderResult.client_secret);
            setShowPaymentModal(true);
        } catch (error) {
            console.error("Stripe payment error:", error);
            displayResultModal(
                "error",
                "Payment Error",
                error.message || "Failed to initiate payment. Please try again."
            );
        } finally {
            setIsProcessing(false);
        }
    };

    // Stripe Elements appearance configuration
    const appearance = {
        theme: "stripe",
        variables: {
            colorPrimary: "#635bff",
            colorBackground: "#ffffff",
            colorText: "#30313d",
            colorDanger: "#df1b41",
            fontFamily: "system-ui, sans-serif",
            spacingUnit: "4px",
            borderRadius: "8px",
        },
        rules: {
            ".Input": {
                border: "1px solid #e0e0e0",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            },
            ".Input:focus": {
                border: "1px solid #635bff",
                boxShadow: "0 0 0 3px rgba(99, 91, 255, 0.2)",
            },
        },
    };

    const elementsOptions = {
        clientSecret,
        appearance,
    };

    return (
        <>
            {/* Stripe Payment Button */}
            <button
                type="button"
                className="btn btn-outline-primary btn-lg w-100"
                onClick={handleStripePayment}
                disabled={disabled || isProcessing || parseFloat(amount) <= 0}
            >
                {isProcessing ? (
                    <>
                        <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                        ></span>
                        Processing...
                    </>
                ) : (
                    <>
                        <i className="fa fa-cc-stripe me-2"></i>
                        Pay {currency === "INR" ? "₹" : "$"}{parseFloat(amount).toFixed(2)} with Stripe
                    </>
                )}
            </button>

            {/* Stripe Payment Modal (Popup) */}
            <Modal
                show={showPaymentModal}
                onHide={handlePaymentModalCancel}
                centered
                backdrop="static"
                keyboard={false}
                size="md"
            >
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>
                        <i className="fa fa-cc-stripe me-2"></i>
                        Stripe Payment
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {/* Payment Summary */}
                    <div className="payment-summary mb-4 p-3 bg-light rounded">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Invoice:</span>
                            <span className="fw-bold">{invoice?.invoice_no}</span>
                        </div>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Amount to Pay:</span>
                            <span className="fw-bold text-primary fs-5">
                                {currency === "INR" ? "₹" : "$"}{parseFloat(amount).toFixed(2)}
                            </span>
                        </div>
                    </div>

                    {/* Stripe Elements */}
                    {clientSecret && (
                        <Elements stripe={stripePromise} options={elementsOptions}>
                            <CheckoutForm
                                invoiceId={invoiceId}
                                invoice={invoice}
                                amount={amount}
                                onSuccess={handlePaymentSuccess}
                                onCancel={handlePaymentModalCancel}
                                onError={handlePaymentError}
                                currency={currency}
                            />
                        </Elements>
                    )}

                    {/* Secure Payment Notice */}
                    <div className="text-center mt-4">
                        <small className="text-muted">
                            <i className="fa fa-lock me-1"></i>
                            Secured by Stripe. Your payment information is encrypted.
                        </small>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Success/Error Result Modal */}
            <SuccessModal
                showModal={showResultModal}
                handleClose={handleResultModalClose}
                type={resultModalConfig.type}
                title={resultModalConfig.title}
                message={resultModalConfig.message}
                primaryButtonText="OK"
            />
        </>
    );
};

export default StripePaymentButton;
